/**
 * Lógica pura de trazabilidad por lote/serie + FEFO (CC-6: NUNCA usar macros
 * lingui aquí).
 *
 * Un producto puede rastrearse por:
 *   - 'none'   : sin lote (comportamiento F1–F3).
 *   - 'lot'    : por número de lote (varias unidades por lote).
 *   - 'serial' : por número de serie (1 unidad por serie).
 *
 * Cuando un producto está rastreado, las recepciones/entregas/ajustes EXIGEN un
 * lote y el on-hand se lleva por (producto × ubicación × lote).
 *
 * FEFO (First-Expired-First-Out): al hacer una salida de un producto rastreado se
 * sugiere/auto-selecciona el lote disponible que vence PRIMERO. Determinista:
 *   1) sólo lotes con on-hand > 0 en la ubicación de origen;
 *   2) ordenados por fecha de caducidad ascendente (los que caducan antes salen
 *      primero); los lotes SIN caducidad van al final (nunca vencen);
 *   3) desempate estable por nombre y luego por id, para resultado reproducible.
 *
 * Cantidades en ENTEROS (unidades menores, factor 1000), igual que F1–F3.
 */

export type ProductTracking = "none" | "lot" | "serial";

export const PRODUCT_TRACKINGS: readonly ProductTracking[] = ["none", "lot", "serial"] as const;

export function isProductTracking(value: string): value is ProductTracking {
  return (PRODUCT_TRACKINGS as readonly string[]).includes(value);
}

/** Normaliza un string (posiblemente nulo) a un ProductTracking válido. */
export function coerceProductTracking(value: string | null | undefined): ProductTracking {
  return value && isProductTracking(value) ? value : "none";
}

/** ¿El producto exige lote en sus movimientos? (rastreado por lote o serie) */
export function isTracked(tracking: string | null | undefined): boolean {
  return coerceProductTracking(tracking) !== "none";
}

/** Lote (subset relevante para FEFO / caducidad). */
export interface LotLike {
  id: string;
  name: string | null;
  /** Fecha de caducidad ISO (YYYY-MM-DD) o null si no caduca. */
  expiry_date: string | null;
}

/** Disponibilidad de un lote en una ubicación (on-hand por lote). */
export interface LotAvailability {
  lot: LotLike;
  /** On-hand del lote en la ubicación de origen (unidades menores, >= 0). */
  onHand: number;
}

/**
 * Ordena lotes por FEFO (vence primero → sale primero). Los lotes sin caducidad
 * (expiry_date null) van AL FINAL. Orden total estable (desempate por nombre→id)
 * para que el resultado sea determinista. No muta la entrada.
 */
export function sortLotsFefo(lots: LotLike[]): LotLike[] {
  return lots.toSorted(compareFefo);
}

/** Comparador FEFO determinista entre dos lotes. */
function compareFefo(a: LotLike, b: LotLike): number {
  const ea = a.expiry_date;
  const eb = b.expiry_date;
  if (ea !== eb) {
    // null = nunca vence → al final.
    if (!ea) {
      return 1;
    }
    if (!eb) {
      return -1;
    }
    return ea < eb ? -1 : 1;
  }
  // Misma caducidad (o ambas null): desempate estable por nombre y luego id.
  const na = a.name ?? "";
  const nb = b.name ?? "";
  if (na !== nb) {
    return na < nb ? -1 : 1;
  }
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/**
 * Selecciona el lote que vence primero ENTRE los que tienen on-hand disponible en
 * la ubicación. Devuelve null si no hay ningún lote con stock. Base de la
 * auto-selección FEFO de una entrega.
 */
export function suggestFefoLot(available: LotAvailability[]): LotLike | null {
  const withStock = available.filter((a) => a.onHand > 0);
  if (withStock.length === 0) {
    return null;
  }
  const sorted = withStock.toSorted((a, b) => compareFefo(a.lot, b.lot));
  return sorted[0]!.lot;
}

/** Una porción de una salida imputada a un lote concreto. */
export interface FefoAllocation {
  lotId: string;
  /** Cantidad tomada de este lote (unidades menores, > 0). */
  qty: number;
}

/**
 * Reparte una cantidad de salida entre los lotes disponibles siguiendo FEFO:
 * agota el lote que vence antes, luego el siguiente, etc. Determinista. Si la
 * demanda excede el on-hand total, devuelve `shortfall` > 0 (faltante no cubierto)
 * y reparte sólo lo disponible.
 */
export function allocateFefo(
  available: LotAvailability[],
  demand: number,
): { allocations: FefoAllocation[]; shortfall: number } {
  if (demand <= 0) {
    return { allocations: [], shortfall: 0 };
  }
  const sorted = available
    .filter((a) => a.onHand > 0)
    .toSorted((a, b) => compareFefo(a.lot, b.lot));
  const allocations: FefoAllocation[] = [];
  let remaining = demand;
  for (const entry of sorted) {
    if (remaining <= 0) {
      break;
    }
    const take = Math.min(entry.onHand, remaining);
    allocations.push({ lotId: entry.lot.id, qty: take });
    remaining -= take;
  }
  return { allocations, shortfall: Math.max(remaining, 0) };
}
